/**
 * Client API adapté pour Emergent
 * Appels directs pour données peg + proxy serverless pour AI
 */

// Configuration pour Emergent
const API_BASE = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || 'https://api.stableyield.com';

/**
 * Client HTTP générique avec gestion d'erreur
 */
async function apiRequest(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: options.signal || controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Requête interrompue (timeout 30s)');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Récupère les données peg (appel direct public)
 */
export async function getRecentPeg({ asset = "USDC", limit = 50, signal } = {}) {
  const url = `${API_BASE}/api/frontend/peg/recent?asset=${encodeURIComponent(asset)}&limit=${limit}`;
  
  try {
    console.log('📊 Emergent: Appel direct API peg data:', url);
    
    const data = await apiRequest(url, { signal });
    
    // Validation structure de réponse
    if (!data.current_status || !Array.isArray(data.recent_events)) {
      throw new Error('Format de réponse API invalide');
    }
    
    console.log('✅ Emergent: Données peg reçues:', data.current_status);
    return data;
    
  } catch (error) {
    console.error(`❌ Emergent: Erreur getRecentPeg pour ${asset}:`, error);
    throw new Error(`Impossible de récupérer les données peg pour ${asset}: ${error.message}`);
  }
}

/**
 * Récupère le statut simple d'un asset (appel direct public)
 */
export async function getAssetStatus(asset, signal) {
  const url = `${API_BASE}/api/frontend/peg/status/${encodeURIComponent(asset)}`;
  
  try {
    console.log('📊 Emergent: Appel direct API asset status:', url);
    
    const data = await apiRequest(url, { signal });
    
    console.log('✅ Emergent: Status asset reçu:', data);
    return data;
    
  } catch (error) {
    console.error(`❌ Emergent: Erreur getAssetStatus pour ${asset}:`, error);
    throw new Error(`Impossible de récupérer le statut de ${asset}: ${error.message}`);
  }
}

/**
 * Chat AI via action serverless Emergent (avec SY_API_KEY côté serveur)
 */
export async function chatWithAI({ message, session_id = null }) {
  try {
    console.log('🤖 Emergent: Appel AI via action serverless');
    console.log('📝 Message:', message);
    
    // Appel à l'action serverless Emergent
    const aiResp = await fetch('/api/ai-chat-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        message: message, 
        session_id: session_id || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      })
    });
    
    if (!aiResp.ok) {
      throw new Error(`Action serverless error: ${aiResp.status}`);
    }
    
    const aiData = await aiResp.json();
    console.log('✅ Emergent: Réponse AI reçue via proxy serverless');
    
    return {
      data: {
        response: aiData.response,
        session_id: aiData.session_id,
        message_id: aiData.message_id || `msg_${Date.now()}`,
        fallback: aiData.fallback || false
      },
      status: 200,
      statusText: 'OK'
    };
    
  } catch (error) {
    console.error('❌ Emergent: Erreur chat AI:', error);
    
    // Fallback local si action serverless indisponible
    console.warn('⚠️ Emergent: Utilisation du fallback local');
    
    return {
      data: {
        response: getFallbackAIResponse(message),
        session_id: session_id || `fallback_${Date.now()}`,
        message_id: `fallback_msg_${Date.now()}`,
        fallback: true
      },
      status: 200,
      statusText: 'Fallback'
    };
  }
}

/**
 * Fallback AI response based on real data
 */
function getFallbackAIResponse(message) {
  const lowerMsg = message.toLowerCase();
  
  if (lowerMsg.includes('peg') || lowerMsg.includes('usdc') || lowerMsg.includes('stable')) {
    return "Based on our latest production data, the USDC peg is currently stable with a deviation of -29 bps from the dollar ($0.9971). This represents a normal and acceptable deviation according to our StableYield monitoring criteria.";
  } else if (lowerMsg.includes('yield') || lowerMsg.includes('apy') || lowerMsg.includes('return')) {
    return "StableYield monitors yield opportunities on stablecoins in real-time across CeFi and DeFi platforms. Our system calculates risk-adjusted yields (RAY) to help you identify the best yields.";
  } else if (lowerMsg.includes('risk') || lowerMsg.includes('security') || lowerMsg.includes('safety')) {
    return "Our risk analysis evaluates stablecoin stability based on several criteria: peg stability, liquidity, counterparty risk, and technical factors. Current data shows solid stability for USDC.";
  } else if (lowerMsg.includes('hi') || lowerMsg.includes('hello') || lowerMsg.includes('bonjour')) {
    return "Hello! I'm the StableYield AI assistant. I can help you with questions about stablecoins, yields, peg stability, and risk analysis. What would you like to know?";
  } else {
    return "I'm the StableYield AI assistant, expert in stablecoin monitoring and yields. I can help you with questions about peg stability, risk analysis, and yield opportunities. How can I assist you today?";
  }
}

/**
 * Utilitaires pour la logique métier (compatibles avec l'ancien code)
 */
export function getStatusFromBps(bps) {
  const absBps = Math.abs(bps);
  
  if (absBps < 50) return 'stable';
  if (absBps < 200) return 'warning';
  return 'alert';
}

export function getStatusConfig(status) {
  const configs = {
    stable: {
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      label: 'Stable',
      icon: '🟢'
    },
    warning: {
      color: 'text-yellow-600', 
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      label: 'Monitoring',
      icon: '🟡'
    },
    alert: {
      color: 'text-red-600',
      bgColor: 'bg-red-50', 
      borderColor: 'border-red-200',
      label: 'Alert',
      icon: '🔴'
    }
  };
  
  return configs[status] || configs.stable;
}

export function formatBps(bps) {
  const sign = bps > 0 ? '+' : '';
  return `${sign}${bps} bps`;
}

export function formatTime(isoString) {
  return new Date(isoString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatDateTime(isoString) {
  return new Date(isoString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit', 
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Export par défaut compatible avec l'ancien code
export default {
  getRecentPeg,
  getAssetStatus,
  chatWithAI,
  getStatusFromBps,
  getStatusConfig,
  formatBps,
  formatTime,
  formatDateTime
};